import { useCallback, useState } from "react";
import { capitalize } from "lodash";

import { copy } from "shared/assets";
import { Band, CaretDown, CaretUp } from "shared/assets/icons";
import {
  Card,
  IActionDropdownItem,
  Pager,
  Table,
  TableColumn,
  TableSkeleton,
} from "shared/components";
import {
  BAND_ACTIONS,
  IBand,
  IBandSubscription,
  SubscriptionStatus,
} from "Bands/types";
import { Spacer } from "shared/layout";
import { buildUrl, formatDateTime } from "shared/utils";
import { usePagination } from "Bands/utils";
import { IAPIState, ITheme } from "shared/types";
import { useAdminContext } from "Bands/contexts";
import { BoldText, Icon, MutedText, SmallText } from "shared/styles";
import { useHistory } from "react-router-dom";
import { BAND_PATHS } from "Bands/router";

const dashboardCopy = copy.adminDashboard;

export const BandsTable = () => {
  const { bands, fetchAdminBandsMeta, fetchAdminBands } = useAdminContext();

  const bandsPagination = usePagination(BAND_ACTIONS.getAdminBands);

  const history = useHistory();

  const [sortBy, setSortBy] = useState<string>("title");
  const [sortDirection, setSortDirection] = useState<string>("asc");

  const getRowActions = useCallback(
    (id: string): IActionDropdownItem[] => {
      const band = bands.find((band) => band.id === id);

      if (!band) return [];

      return [
        {
          label: dashboardCopy.actions.showHistory,
          handleClick: () =>
            history.push(buildUrl(BAND_PATHS.ADMIN_BAND, { bandId: band.id })),
          dataTestId: "show_history_action",
        },
      ];
    },
    [bands, history]
  );

  const handleSort = useCallback(
    (sortBy: string, sortDirection: string) => {
      setSortBy(sortBy);
      setSortDirection(sortDirection);
      fetchAdminBands(1, sortBy, sortDirection);
    },
    [fetchAdminBands]
  );

  const headerComponent = useCallback(
    (copy: string, columnId: string) => {
      return (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            fontSize: sortBy === columnId ? "12px" : "10px",
          }}
          onClick={() => {
            handleSort(columnId, sortDirection === "asc" ? "desc" : "asc");
          }}
        >
          {copy}
          {sortBy === columnId && (
            <Icon
              as={sortDirection === "asc" ? CaretUp : CaretDown}
              size="m"
              style={{
                cursor: "pointer",
              }}
            />
          )}
        </span>
      );
    },
    [handleSort, sortBy, sortDirection]
  );

  const columns: TableColumn[] = [
    {
      Header: headerComponent(dashboardCopy.table.band, "title"),
      accessor: "title",
      Cell: ({ row }) => {
        const band = row.original as IBand;

        return (
          <Spacer size="xs">
            <BoldText>{band.title}</BoldText>
            <SmallText as={MutedText}>{band.id}</SmallText>
          </Spacer>
        );
      },
    },
    {
      Header: headerComponent(dashboardCopy.table.createdAt, "createdAt"),
      accessor: "createdAt",
      Cell: ({ value }) => formatDateTime(value),
    },
    {
      Header: dashboardCopy.table.activePlan,
      accessor: "subscription",
      Cell: ({ value }) => {
        const subscription = value as IBandSubscription;

        if (
          !subscription ||
          subscription.status !== SubscriptionStatus.active
        ) {
          return <SmallText as={MutedText}>none</SmallText>;
        }

        return (
          <span>
            {capitalize(subscription.plan)}
            <br />
            <SmallText as={MutedText}>
              ({formatDateTime(subscription.start, { dateOnly: true })} -{" "}
              {formatDateTime(subscription.stop, { dateOnly: true })})
            </SmallText>
          </span>
        );
      },
    },
    {
      Header: dashboardCopy.table.upcomingPlan,
      // we want to access subscription twice but we can't
      // so we're accessing permissions as a fix
      accessor: "permissions",
      Cell: ({ row }) => {
        const emptyCopy = <SmallText as={MutedText}>none</SmallText>;

        const subscription = (row.original as IBand).subscription;

        if (!subscription) return emptyCopy;

        switch (subscription.status) {
          case SubscriptionStatus.active: {
            if (!subscription.nextPhasePlan || !subscription.nextPhaseStart)
              return emptyCopy;

            return (
              <span>
                {capitalize(subscription.nextPhasePlan)}
                <br />
                <SmallText as={MutedText}>
                  (starts on{" "}
                  {formatDateTime(subscription.nextPhaseStart, {
                    dateOnly: true,
                  })}
                  )
                </SmallText>
              </span>
            );
          }
          case SubscriptionStatus.inactive: {
            return (
              <span>
                {capitalize(subscription.plan)}
                <br />
                <SmallText as={MutedText}>
                  (starts on{" "}
                  {formatDateTime(subscription.start, { dateOnly: true })})
                </SmallText>
              </span>
            );
          }
          default:
            return emptyCopy;
        }
      },
    },
  ];

  return (
    <Card title={dashboardCopy.title} icon={Band}>
      <Spacer size="s">
        {fetchAdminBandsMeta === IAPIState.pending ? (
          <TableSkeleton
            nbRows={bands.length ? bands.length + 1 : 16}
            theme={ITheme.tertiary}
          />
        ) : (
          <Table
            data={bands}
            columns={columns}
            getRowActions={getRowActions}
            emptyCopy={dashboardCopy.table.empty}
          />
        )}

        <Pager
          {...bandsPagination}
          onPageChange={(page) => fetchAdminBands(page, sortBy, sortDirection)}
          isDisabled={fetchAdminBandsMeta === IAPIState.pending}
        />
      </Spacer>
    </Card>
  );
};
